/* eslint-disable restrict-imports/restrict-imports */
/* eslint-disable react/display-name */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMount } from 'react-use-v13';
import { makeStyles } from 'design-system/product/theme';
import { Button } from 'design-system';
// eslint-disable-next-line restrict-imports/restrict-imports
import { AuditResult, fullPageModalLayer } from '@scale/shared/defaults';
import { AuditBatch } from './types';
import TextBasedSpeedAuditHeader from './TextBased/TextBasedSpeedAuditHeader';
import apiFetch from 'react-scale/lib/api-fetch';
import {
  DataGridPro,
  GridColumns,
  GridCellParams,
  GridRenderCellParams,
  useGridApiRef,
  GridColumnResizeParams,
  GridColumnOrderChangeParams,
  GridColumnVisibilityModel,
  gridVisibleRowCountSelector,
  GridFooterContainer,
  GridRenderEditCellParams,
} from '@mui/x-data-grid-pro';
import { TextCollectionResponse } from '@scale/shared/types/response/textcollection';
import _ from 'lodash';
import TaskView from './TextBased/TaskView';
import TextBasedSpeedAuditFlagModal from './TextBased/TextBasedSpeedAuditFlagModal';
import { fetchProcessedResponseLLM, fetchLintResponseVerdict } from '@scale/frontend-lib/linting';
import TextBasedSpeedAuditEditModal, {
  GRAMMAR_SPELLING_LLM_PROMPT,
} from 'react-scale/components/audits/SpeedAudit/TextBased/TextBasedSpeedAuditEditModal';
// eslint-disable-next-line import/no-extraneous-dependencies
import { LicenseInfo } from '@mui/x-license-pro';
import useShortcut from 'react-scale/hooks/useShortcut';
import Promise from 'bluebird';
import useAuditErrorCategories from 'react-scale/components/audits/SpeedAudit/v2/hooks/UseAuditErrorCategories';
import FindAndReplace from './TextBased/FindAndReplace';
import SmartLink from 'react-scale/corp/components/SmartLink/SmartLink';
import { INamedObjectTypes } from '@scale/shared/types/lookup';
import {
  saveColumnConfig,
  getColumnConfig,
  applyColumnConfig,
  getCustomColumnConfigs,
} from './columnConfigHelpers';
import AuditClaimedTasksDialog from './TextBased/AuditClaimedTasksDialog';
import {
  QAOperationContextProvider,
  useQAOperationContext,
} from 'react-scale/components/remoAdmin/qaOperation/qaOperationContext';
import { AttemptCustomColumn, AuditLookupType } from '@scale/shared/types/corp/attemptAudit';
import {
  TextWrapComponent,
  TextDiffComponent,
  AcceptLLMEdits,
} from './TextBased/tbsaCellComponents';
import {
  getDefaultColumns,
  levenshtein,
  getRowFromAttemptAudit,
  LockedColumnHeader,
  getNestedFields,
} from './TextBased/tbsaUtils';
import { FieldsInfo, PastActions, ResponseField } from './TextBased/tbsaTypes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TextBasedSpeedAuditShortcutModal from './TextBased/TextBasedSpeedAuditShortcutModal';
import TextBasedSpeedAuditManageColumns from './TextBased/TextBasedSpeedAuditManageColumns';
import SAlert from 'react-s-alert';
import {
  IMandatoryColumn,
  IColorColumn,
  ConditionOperations,
} from '@scale/shared/types/corp/speedAuditColumnConfig';
import { maybePropagateResponseChanges } from '@scale/shared/chat/response';

export enum TextBasedSpeedAuditEditModes {
  Flag = 'flag',
  Edit = 'edit',
}

const RESERVED_COL_NAMES = ['flag', 'edited', 'diff', 'levenshtein', 'copy_edit'];
const LLM_RESERVED_COL_NAMES = ['edited_', 'diff_', 'levenshtein_', 'copy_edit_'];

LicenseInfo.setLicenseKey(
  'a38ef84cf2c86027ac1b0b08d0b5b092Tz01NzkzNCxFPTE3MDU2MTIyNTgyNzksUz1wcm8sTE09c3Vic2NyaXB0aW9uLEtWPTI=',
);

function TextBasedSpeedAuditDialog({
  auditBatch,
  projectId,
  objectIds,
  isPodHealth = false,
  setIsAuditing = (_isAuditing: boolean) => {},
}: {
  auditBatch: AuditBatch;
  projectId: string;
  objectIds: string[];
  isPodHealth?: boolean;
  setIsAuditing?: (isAuditing: boolean) => void;
}) {
  const [cellFontSize, setCellFontSize] = useState<number>(14);
  const styles = useTextBasedSpeedAuditDialogStyles({ cellFontSize });
  const apiRef = useGridApiRef();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const attemptAuditsRef = useRef<any[]>([]);
  const [columns, setColumns] = useState<GridColumns>([]);
  const [rows, setRows] = useState<Record<string, any>[]>([]);

  const [fields, setFields] = useState<Record<string, FieldsInfo>>({});
  const [mode, setMode] = useState(undefined);
  const [showFlagDialog, setShowFlagDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ratingSelected, setRatingSelected] = useState<string | undefined>();
  const [completed, setCompleted] = useState<boolean>(!!auditBatch.completed);
  const [pastActions, setPastActions] = useState<PastActions>({ pastActions: [], index: -1 });
  const [isFindAndReplaceMode, setIsFindAndReplaceMode] = useState<boolean>(false);
  const [columnConfig, setColumnConfig] = useState<GridColumns>([]);
  const [columnVisibilityConfig, setColumnVisibilityConfig] = useState<GridColumnVisibilityModel>(
    {},
  );
  const [mandatoryColumns, setMandatoryColumns] = useState<IMandatoryColumn>({});
  const [colorColumns, setColorColumns] = useState<IColorColumn>({});

  const [columnConfigChanged, setColumnConfigChanged] = useState<boolean>(false);
  const [runColumnConfig, setRunColumnConfig] = useState<boolean>(false);
  const [isTaskView, setIsTaskView] = useState<boolean>(false);
  const [selectedTaskProps, setSelectedTaskProps] = useState<any>();
  const [selectedTaskIndex, setSelectedTaskIndex] = useState<number>(0);
  const [claimedTasksInfo, setClaimedTasksInfo] = useState<Record<string, any>[]>([]);
  const [visibleRows, setVisibleRows] = React.useState<number>();
  const { errorCategories: possibleErrorCategories } = useAuditErrorCategories(projectId);
  const [collapseRows, setCollapseRows] = useState<boolean>(false);
  const [canEditResponses, setCanEditResponses] = useState<boolean>(true);
  const [usesPipelineV3, setUsesPipelineV3] = useState<boolean>(false);
  const [isChatTask, setIsChatTask] = useState<boolean>(false);
  const [showShortcutModal, setShowShortcutModal] = useState<boolean>(false);
  const [showManageColumns, setShowManageColumns] = useState<boolean>(false);

  const { qaOperation } = useQAOperationContext();

  const updateCompletionCounts = () => {
    const updatedRows = Array.from(apiRef.current.getRowModels().values());
    return _.countBy(updatedRows, row => row.rating ?? 'No rating');
  };

  React.useEffect(() => {
    apiRef.current.subscribeEvent('stateChange', () => {
      const count = gridVisibleRowCountSelector(apiRef.current.state);
      setVisibleRows(count);
    });
  }, [apiRef]);

  useEffect(() => {
    setCompleted(!!auditBatch.completed);
  }, [auditBatch]);

  useEffect(() => {
    if (!_.isEmpty(possibleErrorCategories)) {
      saveAudit();
      fetchAttemptAudits();
    }
  }, [possibleErrorCategories]);

  // Rating shortcuts
  useShortcut({
    trigger: 'a',
    action: () => {
      if (!ratingSelected) {
        return;
      }
      updatePastActions(
        [{ task_links: ratingSelected, rating: AuditResult.Perfect }],
        [{ task_links: ratingSelected, rating: apiRef.current.getRow(ratingSelected).rating }],
      );
      apiRef.current.updateRows([{ task_links: ratingSelected, rating: AuditResult.Perfect }]);
    },
    description: 'Set Audit Result to Perfect',
    disabled: _.isNil(ratingSelected),
  });
  useShortcut({
    trigger: 'w',
    action: () => {
      if (!ratingSelected) {
        return;
      }
      updatePastActions(
        [{ task_links: ratingSelected, rating: AuditResult.Good }],
        [{ task_links: ratingSelected, rating: apiRef.current.getRow(ratingSelected).rating }],
      );
      apiRef.current.updateRows([{ task_links: ratingSelected, rating: AuditResult.Good }]);
    },
    description: 'Set Audit Result to Good',
    disabled: _.isNil(ratingSelected),
  });
  useShortcut({
    trigger: 'd',
    action: () => {
      if (!ratingSelected) {
        return;
      }
      updatePastActions(
        [{ task_links: ratingSelected, rating: AuditResult.Ok }],
        [{ task_links: ratingSelected, rating: apiRef.current.getRow(ratingSelected).rating }],
      );
      apiRef.current.updateRows([{ task_links: ratingSelected, rating: AuditResult.Ok }]);
    },
    description: 'Set Audit Result to Ok',
    disabled: _.isNil(ratingSelected),
  });
  useShortcut({
    trigger: 'r',
    action: () => {
      if (!ratingSelected) {
        return;
      }
      updatePastActions(
        [{ task_links: ratingSelected, rating: AuditResult.Bad }],
        [{ task_links: ratingSelected, rating: apiRef.current.getRow(ratingSelected).rating }],
      );
      apiRef.current.updateRows([{ task_links: ratingSelected, rating: AuditResult.Bad }]);
    },
    description: 'Set Audit Result to Bad',
    disabled: _.isNil(ratingSelected),
  });
  useShortcut({
    trigger: 'f',
    action: () => {
      if (!ratingSelected) {
        return;
      }
      updatePastActions(
        [{ task_links: ratingSelected, rating: AuditResult.Terrible }],
        [{ task_links: ratingSelected, rating: apiRef.current.getRow(ratingSelected).rating }],
      );
      apiRef.current.updateRows([{ task_links: ratingSelected, rating: AuditResult.Terrible }]);
    },
    description: 'Set Audit Result to Terrible',
    disabled: _.isNil(ratingSelected),
  });

  const updatePastActions = (change: Record<string, any>[], snapshot: Record<string, any>[]) => {
    // checks if every change in the change array has a corresponding row in the snapshot array with the exact same values
    const noChange = _.every(change, changeRow =>
      _.some(
        snapshot,
        snapshotRow =>
          snapshotRow.task_links === changeRow.task_links &&
          _.every(Object.keys(changeRow), key => snapshotRow[key] === changeRow[key]),
      ),
    );
    if (noChange) {
      return;
    }
    setPastActions(pa => {
      const actionsToKeep = pa.pastActions.slice(0, pa.index + 1);
      const actions = actionsToKeep.concat([{ undo: snapshot, redo: change }]);
      const lastFiveActions = actions.slice(-5);
      return {
        pastActions: lastFiveActions,
        index: lastFiveActions.length - 1,
      };
    });
  };

  const undo = () => {
    if (pastActions.index === -1) {
      return;
    }
    if (pastActions.pastActions[pastActions.index]) {
      apiRef.current.updateRows(pastActions.pastActions[pastActions.index]?.undo);
      setPastActions(pa => ({
        pastActions: pa.pastActions,
        index: pa.index - 1,
      }));
    }
  };

  const redo = () => {
    if (pastActions.index === pastActions.pastActions.length - 1) {
      return;
    }
    if (pastActions.pastActions[pastActions.index + 1]) {
      apiRef.current.updateRows(pastActions.pastActions[pastActions.index + 1]?.redo);
      setPastActions(pa => ({
        pastActions: pa.pastActions,
        index: pa.index + 1,
      }));
    }
  };

  useShortcut({
    trigger: 'ctrl+z',
    action: undo,
    description: 'Undo the last change',
  });

  useShortcut({
    trigger: 'ctrl+shift+z',
    action: redo,
    description: 'Redo the last change',
  });

  useShortcut({
    trigger: 'ctrl+f',
    action: e => {
      setIsFindAndReplaceMode(true);
      e.preventDefault();
    },
    description: 'Find and Replace',
  });

  const customFooterComponent = () => {
    const numFilters = apiRef.current.state.filter.filterModel.items.length;
    const numQuickFilters = apiRef.current.state.filter.filterModel.quickFilterValues?.length;
    const updatedRows = Array.from(apiRef.current.getRowModels().values());
    const numEdited = _.sumBy(updatedRows, row => {
      const editableFieldChanged = _.some(Object.keys(row), key => {
        return key.endsWith('_editable') && !!row[key];
      });
      const edited =
        row.isEdited || editableFieldChanged || row.rating || row.notes || row.taskerFeedback;
      return edited ? 1 : 0;
    });
    return (
      <GridFooterContainer
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          padding: '0px 16px',
        }}
      >
        <a className={styles.textButton} onClick={() => setShowShortcutModal(true)}>
          <FontAwesomeIcon size="sm" icon="keyboard" />
          Keyboard Shortcuts
        </a>
        <div>
          <div>
            Completed Rows: {numEdited} of {attemptAuditsRef.current.length}
          </div>
          <div>
            Total Rows: {visibleRows}{' '}
            {numFilters || numQuickFilters ? `of ${attemptAuditsRef.current.length}` : ''}
          </div>
        </div>
      </GridFooterContainer>
    );
  };

  const exportDataAsCsv = () => {
    apiRef.current.exportDataAsCsv({ fileName: auditBatch.name });
  };

  const taskViewSetup = (taskId: string, row: any) => {
    const updatedRows = apiRef.current.getRowModels();
    const rowIndex = _.findIndex(Array.from(updatedRows.values()), row => row.task_id === taskId);

    const aaRef: Record<string, any>[] = attemptAuditsRef.current!;
    const attemptAudit = aaRef!.filter((attemptAudit: any) => attemptAudit.taskId === taskId)[0];

    const taskProps = attemptAudit.taskProps;
    taskProps.initialResponse = taskProps.response ?? {};

    const newResponse = getResponseFromRow(row);

    taskProps.initialResponse = newResponse;
    taskProps.isAuditMode = true;
    taskProps.customFunction = (finalResponseToBeSubmitted: TextCollectionResponse, _taskId: any) =>
      customTaskSubmitHandler(finalResponseToBeSubmitted, row.task_links);

    setSelectedTaskIndex(rowIndex);
    setSelectedTaskProps(taskProps);
    setIsTaskView(true);
  };

  const handleSelectedTaskIndexChange = (index: number) => {
    const updatedRows = apiRef.current.getRowModels();
    const row = Array.from(updatedRows.values())[index];

    taskViewSetup(row.task_id, row);
  };

  const onViewNextTask = () => {
    const index = selectedTaskIndex + 1;
    handleSelectedTaskIndexChange(index);
  };

  const onViewPreviousTask = () => {
    const index = selectedTaskIndex - 1;
    handleSelectedTaskIndexChange(index);
  };

  // move this to tbsaCellComponents file once we separate task view
  const taskWrapComponent = (params: GridRenderCellParams) => {
    const isTrainingAttempt = params.row.attemptAuditType === AuditLookupType.TRAINING_ATTEMPT;
    return isTrainingAttempt ? (
      <div className={styles.taskWrapComponent}>
        <SmartLink type={INamedObjectTypes.TrainingAttempt} id={params.row.attempt_id} />
      </div>
    ) : (
      <div className={styles.taskWrapComponent}>
        <SmartLink type={INamedObjectTypes.Task} id={params.row.task_id} />
        {params.row.subtask_id && (
          <SmartLink type={INamedObjectTypes.Subtask} id={params.row.subtask_id} />
        )}
        <SmartLink type={INamedObjectTypes.TaskAttempt} id={params.row.attempt_id} />
      </div>
    );
  };

  // move this to tbsaCellComponents file once we separate task view
  const taskActionsComponent = (params: GridRenderCellParams) => {
    const attemptAudits = attemptAuditsRef.current;
    const isTextCollection =
      attemptAudits && attemptAudits[0] && attemptAudits[0].taskProps.type === 'textcollection';
    return (
      <div>
        {isTextCollection && (
          <Button
            className={styles.actionButton}
            variant="outlined"
            onClick={() => taskViewSetup(params.row.task_id, params.row)}
          >
            View Task in audit
          </Button>
        )}
      </div>
    );
  };

  const fetchAttemptAudits = async (skipRunColumnConfig?: boolean) => {
    try {
      setLoading(true);
      const { canEditResponses, attemptAudits, usesPipelineV3 } = await apiFetch(
        null,
        `corp-api/attemptAudit/attemptAuditsByBatch/${auditBatch._id}`,
        {
          method: 'PUT',
        },
      );
      setCanEditResponses(canEditResponses);
      setUsesPipelineV3(usesPipelineV3);
      const customColumns = await getCustomColumnConfigs(projectId);
      attemptAuditsRef.current = attemptAudits;

      const responseFieldNames: ResponseField[] = [];
      let numTextAttachments: number = 0;
      let hasFairPrompt = false;
      const editableColumns: AttemptCustomColumn[] =
        attemptAudits.length > 0 ? attemptAudits[0].customColumns : [];
      const fixedColumns: string[] =
        attemptAudits.length > 0 ? Object.keys(attemptAudits[0].fixedColumns) : [];

      const tempRows: any[] = attemptAuditsRef.current.map((attemptAudit: any) => {
        const rowResult = getRowFromAttemptAudit(attemptAudit);
        responseFieldNames.push(...rowResult.visibleFields);
        numTextAttachments = Math.max(numTextAttachments, rowResult.numTextAttachments);
        hasFairPrompt ||= rowResult.hasFairPrompt;
        setIsChatTask(rowResult.isChatTask);
        return rowResult.result;
      });

      setFields(
        Object.fromEntries(
          _.uniqBy(responseFieldNames, field => field.path).map(field => [
            field.colName,
            { path: field.path, name: field.fieldName },
          ]),
        ),
      );

      const taskWrapColumn = {
        field: 'task_links',
        headerName: `Task/Subtask IDs`,
        renderHeader: () => LockedColumnHeader('Task/Subtask IDs'),
        width: 300,
        renderCell: (params: GridRenderCellParams) => taskWrapComponent(params),
      };

      const viewTaskColumn = {
        field: 'task_actions',
        headerName: `Task/Subtask Actions`,
        renderHeader: () => LockedColumnHeader('Task/Subtask Actions'),
        width: 200,
        renderCell: (params: GridRenderCellParams) => taskActionsComponent(params),
      };

      const tempColumns = getDefaultColumns({
        canEditResponses,
        isComplete: !!auditBatch.completed,
        editableColumns,
        fixedColumns,
        numTextAttachments,
        responseFields: _.uniqBy(responseFieldNames, field => field.path).map(
          field => field.colName,
        ),
        hasFairPrompt,
        setRatingSelected,
        styles,
        updatePastActions,
        possibleErrorCategories,
        mandatoryColumns,
        customFormulaColumns: customColumns.customFormulaColumns,
      });
      setRows(tempRows);
      setColumns([viewTaskColumn, taskWrapColumn, ...tempColumns]);
      if (!runColumnConfig && !skipRunColumnConfig) {
        setRunColumnConfig(true);
      }
    } catch (ex) {
      SAlert.error(`Error fetching audits: ${ex.toString()}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId && runColumnConfig) {
      (async () => {
        const colConfig: any = await getColumnConfig(projectId);
        if (Object.keys(colConfig)) {
          setColumns(prevColumns => applyColumnConfig(prevColumns, colConfig));
          setColumnVisibilityConfig(colConfig.visibilityConfig ?? {});
          setMandatoryColumns(colConfig.mandatoryColumns ?? {});
          setColorColumns(colConfig.colorColumns ?? {});
        }
        setRunColumnConfig(false);
      })();
    }
  }, [projectId, runColumnConfig]);

  useEffect(() => {
    if (!_.isEmpty(mandatoryColumns)) {
      saveAudit();
      fetchAttemptAudits(true);
    }
  }, [mandatoryColumns]);

  const resetAfterModeChange = useCallback(
    (mode: any) => {
      if (mode === undefined) {
        // clear columns from other modes
        if (columns && columns.length > 0) {
          const tempRows = _.cloneDeep(Array.from(apiRef.current.getRowModels().values()));
          const tempColumns = _.cloneDeep(
            _.filter(
              columns,
              column =>
                !(
                  RESERVED_COL_NAMES.includes(column.field) ||
                  _.some(LLM_RESERVED_COL_NAMES, reservedName =>
                    _.includes(column.field, reservedName),
                  )
                ),
            ),
          );

          tempRows.map(row => {
            RESERVED_COL_NAMES.map(col => delete row[col]);
            LLM_RESERVED_COL_NAMES.map(reservedName => {
              const regexColumns = Object.keys(row).filter(rowColumn =>
                _.includes(rowColumn, reservedName),
              );
              regexColumns.map(col => delete row[col]);
            });
          });
          setColumns(tempColumns);
          setRows(tempRows);
        }
      } else if (mode === TextBasedSpeedAuditEditModes.Edit) {
        setShowEditDialog(true);
      } else if (mode === TextBasedSpeedAuditEditModes.Flag) {
        setShowFlagDialog(true);
      }
    },
    [rows, columns],
  );

  useEffect(() => {
    resetAfterModeChange(mode);
  }, [mode]);

  useMount(() => {
    fetchAttemptAudits();
  });

  const getResponseFromRow = (row: any) => {
    const attemptAudit = attemptAuditsRef.current.filter(
      (attemptAudit: any) => attemptAudit.taskId === row.task_id,
    )[0];
    const clonedResponse = _.cloneDeep(attemptAudit.newResponse);
    for (const fieldName of Object.keys(fields)) {
      const path = fields[fieldName].path;
      if (_.has(row, fieldName)) {
        _.set(clonedResponse, path, row[fieldName]);
      }
    }
    return clonedResponse;
  };

  const chatTaskResponseFormatting = (
    response: Record<string, any>,
    fieldName: string,
    annotationName: string,
  ) => {
    const currValue = _.get(response, fields[fieldName].path, '');
    const annotations = response.annotations ?? {};
    _.set(annotations, annotationName, { response: [currValue] });
    return {
      ...response,
      annotations,
    };
  };

  const processPromptWithResponse = (row: any, prompt: string) => {
    let updated = prompt;
    for (const fieldName of Object.keys(fields)) {
      if (_.has(row, fieldName)) {
        updated = updated.replace(`{{${fieldName}}}`, row[fieldName]);
      }
    }
    return updated;
  };

  const flagTasks = useCallback(
    async (fieldsToApply: string[], prompt?: string) => {
      try {
        setLoading(true);
        const tempRows = _.cloneDeep(Array.from(apiRef.current.getRowModels().values()));
        const tempColumns = [
          {
            field: 'flag',
            headerName: 'Flagged',
            renderHeader: () => LockedColumnHeader('Flagged'),
            renderCell: (p: GridRenderEditCellParams) => (
              <TextWrapComponent params={p} styles={styles} />
            ),
          },
          ...columns,
        ];
        tempRows.map(row => (row.flag = 'Loading...'));
        setRows(tempRows);
        setColumns(tempColumns);
        await Promise.all(
          tempRows.map(async row => {
            const attemptAudit = attemptAuditsRef.current.filter(
              (attemptAudit: any) => attemptAudit.taskId === row.task_id,
            )[0];
            const fieldRules = fieldsToApply.map(fieldId => {
              return {
                fieldId,
                language: 'english',
                minTokensToApply: 5,
              };
            });
            const currResponse = getResponseFromRow(row);
            const fieldsInput = fieldsToApply.map(fieldName => fields[fieldName].name);
            let responsesInput = currResponse;
            if (isChatTask) {
              fieldsToApply.map(fieldName => {
                responsesInput = chatTaskResponseFormatting(
                  responsesInput,
                  fieldName,
                  fields[fieldName].name,
                );
              });
            }

            if (prompt) {
              const verdict = await fetchProcessedResponseLLM({
                responses: [responsesInput],
                fields: fieldsInput,
                ...(prompt !== undefined && { prompt: processPromptWithResponse(row, prompt) }),
              });
              if (_.isEmpty(verdict)) {
                throw new Error(
                  `Received empty response from LLM for prompt ${processPromptWithResponse(
                    row,
                    prompt,
                  )}`,
                );
              }
              const flag = _.get(verdict[0].metadata, `${fieldsInput[0]}.llmResponse`, undefined);
              row.flag = flag;
            } else {
              const verdict = await fetchLintResponseVerdict({
                taskProps: attemptAudit.taskProps,
                response: responsesInput,
                lintRules: [
                  {
                    stage: 'frontend',
                    frontendSeverity: 'ERROR',
                    enableAttempt: true,
                    enableCustomerAudit: true,
                    name: 'language-mk7h69',
                    message: '',
                    level: 'info',
                    origin: 'customer',
                    condition: {
                      scriptName: 'SCRIPT_text-field-correct-language-linter',
                      config: {
                        fieldRules,
                      },
                      validatorType: 'VALIDATOR_language',
                    },
                  },
                ],
              });
              if (_.isEmpty(verdict) || _.isEmpty(verdict.lintResults)) {
                throw new Error(`Received empty response from LLM for Gibberish linter`);
              }
              row.flag = verdict.lintResults[0].lintOutcome;
            }
            apiRef.current.updateRows([row]);
          }),
        );
        setLoading(false);
      } catch (e: any) {
        setLoading(false);
      }
    },
    [rows, columns],
  );

  const editField = useCallback(
    async (fieldsToApply: string[], prompt?: string) => {
      try {
        setLoading(true);

        // create columns and set temp loading rows
        const tempRows = _.cloneDeep(Array.from(apiRef.current.getRowModels().values()));
        const tempColumns = [
          ..._.flattenDeep(
            fieldsToApply.map(fieldName => {
              return [
                {
                  field: `copy_edit_${fieldName}`,
                  headerName: `Accept ${fieldName}`,
                  width: 150,
                  renderCell: (p: GridRenderEditCellParams) => (
                    <AcceptLLMEdits
                      params={p}
                      updatePastActions={updatePastActions}
                      editable={!completed}
                      styles={styles}
                    />
                  ),
                },
                {
                  field: `edited_${fieldName}`,
                  headerName: `Edited Response ${fieldName}`,
                  renderHeader: () => LockedColumnHeader(`Edited Response ${fieldName}`),
                  width: 250,
                  renderCell: (p: GridRenderEditCellParams) => (
                    <TextWrapComponent params={p} styles={styles} />
                  ),
                },
                {
                  field: `diff_${fieldName}`,
                  headerName: `Diff ${fieldName}`,
                  renderHeader: () => LockedColumnHeader(`Diff ${fieldName}`),
                  width: 250,
                  renderCell: (p: GridRenderEditCellParams) => (
                    <TextDiffComponent params={p} styles={styles} />
                  ),
                },
                {
                  field: `levenshtein_${fieldName}`,
                  headerName: `Levenshtein ${fieldName}`,
                  width: 150,
                  renderHeader: () => LockedColumnHeader(`Levenshtein ${fieldName}`),
                },
              ];
            }),
          ),
          ...columns,
        ];
        tempRows.map(row => (row.edited = 'Loading edits...'));
        setRows(tempRows);
        setColumns(tempColumns);

        await Promise.all(
          tempRows.map(async row => {
            const currResponse: any = getResponseFromRow(row);
            await fieldsToApply.map(async fieldName => {
              let auxPrompt = prompt;
              if (!auxPrompt) {
                auxPrompt = GRAMMAR_SPELLING_LLM_PROMPT;
              }
              auxPrompt = auxPrompt.replace('{{value}}', `{{${fieldName}}}`);
              const annotationName = fields[fieldName].name;
              const annotationPath = `${annotationName}.llmResponse`;
              let responsesInput = currResponse;
              if (isChatTask) {
                responsesInput = chatTaskResponseFormatting(
                  currResponse,
                  fieldName,
                  annotationName,
                );
              }
              const verdict = await fetchProcessedResponseLLM({
                responses: [responsesInput],
                fields: [annotationName],
                ...(auxPrompt !== undefined && {
                  prompt: processPromptWithResponse(row, auxPrompt),
                }),
              });
              if (_.isEmpty(verdict)) {
                throw new Error(
                  `Received empty response from LLM for ${
                    auxPrompt
                      ? `prompt ${processPromptWithResponse(row, auxPrompt)}`
                      : 'default prompt'
                  }`,
                );
              }
              const originalResponse = _.get(currResponse, fields[fieldName].path, undefined);
              const editedResponse = _.get(verdict[0].metadata, annotationPath, undefined);
              row[`edited_${fieldName}`] = editedResponse;
              row[`diff_${fieldName}`] = JSON.stringify({
                newText: editedResponse,
                originalText: originalResponse,
              });
              row[`copy_edit_${fieldName}`] = JSON.stringify({
                field: fieldName,
                newText: editedResponse,
                originalText: originalResponse,
              });
              row[`levenshtein_${fieldName}`] = levenshtein(editedResponse, originalResponse);
              apiRef.current.updateRows([row]);
            });
          }),
        );
        setLoading(false);
      } catch (e: any) {
        alert(`Error editing field: ${e.toString()}`);
        setLoading(false);
      }
    },
    [rows, columns],
  );

  const onCancelFindAndReplace = (find: string, column: string) => {
    const filters = apiRef.current.state.filter.filterModel.items;
    const quickFilters = apiRef.current.state.filter.filterModel.quickFilterValues;

    if (quickFilters?.includes(find)) {
      apiRef.current.setQuickFilterValues([]);
    }
    if (_.some(filters, item => item.value === find && item.columnField === column)) {
      const item = filters.find(item => item.value === find && item.columnField === column);
      apiRef.current.deleteFilterItem(item!);
    }
  };

  const onCloseFindAndReplace = () => {
    setIsFindAndReplaceMode(false);
  };

  const onFindAndReplace = async (find: string, replace: string, column: string) => {
    setLoading(true);
    if (!replace && !column) {
      apiRef.current.setQuickFilterValues([find]);
    } else if (!replace && column) {
      apiRef.current.upsertFilterItem({
        columnField: column,
        value: find,
      });
    } else if (replace && column) {
      const tempRows = _.cloneDeep(Array.from(apiRef.current.getRowModels().values()));
      const snapshot: Record<string, any>[] = [];
      const changes: Record<string, any>[] = [];
      await Promise.all(
        tempRows.map(async row => {
          if (!row[column].includes(find)) {
            return;
          } else {
            const oldValue = { task_links: row.task_links };
            _.set(oldValue, column, row[column]);
            snapshot.push(oldValue);
            const findRegex = new RegExp(find, 'g');
            row[column] = row[column].replace(findRegex, replace);
            if (row.has(`${column}_diff`)) {
              row[`${column}_diff`] = {
                originalText: row[`${column}_diff`].originalText,
                newText: row[column],
              };
            }
            changes.push(row);
            apiRef.current.updateRows([row]);
          }
        }),
      );
      updatePastActions(changes, snapshot);
    }
    setLoading(false);
  };

  const saveAudit = async () => {
    const updatedRows = apiRef.current.getRowModels();
    const batchUpdateItems: any[] = [];

    Array.from(updatedRows.values()).forEach(async row => {
      const oldAttemptAudit = attemptAuditsRef.current.filter(
        (attemptAudit: any) => attemptAudit._id === row.attempt_audit_id,
      )[0];
      const newResponse = oldAttemptAudit.newResponse;
      const customColumns = oldAttemptAudit.customColumns;
      if (canEditResponses) {
        for (const fieldName of Object.keys(fields)) {
          const fieldPath = fields[fieldName].path;
          if (_.has(row, fieldName)) {
            if (!_.isNil(newResponse) && !_.isEmpty(newResponse)) {
              _.set(newResponse, fieldPath, row[fieldName]);

              // need to propagate response for chat task
              if (isChatTask) {
                // fieldPath will be a value like 'responses[2].output'. we need to extract the index from it
                const index = fieldPath.match(/\[(\d+)\]/)?.[1];
                if (index !== undefined) {
                  maybePropagateResponseChanges(
                    oldAttemptAudit.taskProps.params,
                    newResponse.responses,
                    Number(index),
                  );
                }
              }

              // extra step for validation, just to be safe before saving incorrectly formatted responses
              const fieldValue = _.get(newResponse, fieldPath, undefined);
              if (_.isNil(fieldValue) || !_.isString(fieldValue)) {
                throw new Error(
                  `Unexpected response format for task ${
                    row.task_id
                  } and field '${fieldName}' in the audit. Response is "${JSON.stringify(
                    row[fieldName],
                  )}" but it should be a string`,
                );
              }
            }
          }
        }
      }
      for (const col of customColumns) {
        const field = `${col.name}_editable`;
        if (_.has(row, field)) {
          _.set(col, 'value', row[field]);
        }
      }

      if (row.fairPrompt !== undefined) {
        if (newResponse) {
          _.set(
            newResponse,
            'annotations.rlhf_history.response[0].interactions[0].prompt',
            row.fairPrompt,
          );
        }
      }

      batchUpdateItems.push({
        attemptAuditId: row.attempt_audit_id,
        result: row.rating,
        resultV2: {
          result: row.rating,
          errorCategories: row.errorCategories,
          attemptId: row.attempt_id,
        },
        comments: row.notes,
        taskerFeedback: row.taskerFeedback,
        newResponse,
        customColumns,
      });
      apiRef.current.updateRows([
        {
          task_links: row.task_links,
          lastSavedResponse: _.cloneDeep(newResponse),
        },
      ]);
    });
    await apiFetch(null, `corp-api/attemptAudit/attemptAudits/batchUpdate`, {
      method: 'POST',
      body: { batchUpdateItems, auditBatchId: auditBatch._id },
    });
    if (auditBatch.claimTasks) {
      await handleRefreshClaims(false);
    }
    if (columnConfigChanged) {
      await saveColumnConfig(
        projectId,
        columnConfig,
        columnVisibilityConfig,
        mandatoryColumns,
        colorColumns,
        false,
      );
      setColumnConfigChanged(false);
    }
  };

  const handleRefreshClaims = async (forceClaim: boolean) => {
    const { claimedTasksInfo } = await apiFetch(
      null,
      `corp-api/attemptAudit/attemptAudits/refreshClaims`,
      {
        method: 'POST',
        body: { auditBatchId: auditBatch._id, forceClaim },
      },
    );
    if (forceClaim) {
      setClaimedTasksInfo([]);
    } else {
      setClaimedTasksInfo(claimedTasksInfo);
    }
  };

  const completeAudit = async (actions: Record<string, any>, sendFeedback: boolean) => {
    await saveAudit();
    const { courseIds, sendBackTasksTemporal } = await apiFetch(
      null,
      `corp-api/attemptAudit/textBasedAudit/complete`,
      {
        method: 'POST',
        body: {
          auditBatchId: auditBatch._id,
          actions,
          sendFeedback,
        },
      },
    );
    auditBatch.completed = true;
    let message = courseIds
      .map(
        (course: Record<string, string>) =>
          `Created course ${course.courseId} for ${course.taskerEmail}`,
      )
      .join('\n');
    message += !_.isEmpty(sendBackTasksTemporal)
      ? `\nKicked off workflow to send tasks back. Use the following IDs to check on the status. Workflow ID: ${sendBackTasksTemporal.temporalWorkflowId}, Run ID: ${sendBackTasksTemporal.temporalRunId}`
      : '';
    if (message) {
      alert(message);
    }
    setCompleted(true);
    const closeOnComplete = window.location.href.includes('closeOnComplete');
    if (closeOnComplete) {
      window.close();
    }
  };

  const handleColumnWidthChange = (params: GridColumnResizeParams) => {
    const newColumns = _.cloneDeep(columns);
    const i = newColumns.findIndex(c => c.field === params.colDef.field);
    newColumns[i].width = params.width;
    setColumns(newColumns);
    const newColumnConfig = newColumns.map(col => _.pick(col, ['field', 'width']));
    setColumnConfig(newColumnConfig);
    setColumnConfigChanged(true);
  };

  const handleColumnOrderChange = (params: GridColumnOrderChangeParams) => {
    const newColumns = _.cloneDeep(columns);
    const element = newColumns[params.oldIndex];
    newColumns.splice(params.oldIndex, 1);
    newColumns.splice(params.targetIndex, 0, element);
    setColumns(newColumns);
    const newColumnConfig = newColumns.map(col => _.pick(col, ['field', 'width']));
    setColumnConfig(newColumnConfig);
    setColumnConfigChanged(true);
  };

  const handleColumnVisibilityModelChange = (newModel: GridColumnVisibilityModel) => {
    setColumnVisibilityConfig(newModel);
    setColumnConfigChanged(true);
  };

  // runs on submit in the task modal- it updates the columns in relevant row to mirror the changes from the submitted response
  const customTaskSubmitHandler = (
    finalResponseToBeSubmitted: TextCollectionResponse,
    rowId: string,
  ) => {
    if (!completed) {
      const newResponseFields = _.flatten(
        Object.values(finalResponseToBeSubmitted.annotations).map((field: any) =>
          getNestedFields(field, 'annotations', ''),
        ),
      );
      const newResponseFieldsObj = Object.fromEntries(
        newResponseFields.map(field => [field.colName, field.value]),
      );

      apiRef.current.updateRows([{ task_links: rowId, ...newResponseFieldsObj }]);
    } else {
      alert(`This audit is already completed, so submitting this task will not change anything.`);
    }
  };

  return (
    <div className={styles.fullPageModal}>
      {showManageColumns && (
        <TextBasedSpeedAuditManageColumns
          modalOpen={showManageColumns}
          setModalOpen={setShowManageColumns}
          columns={columns}
          setColumns={setColumns}
          visibilityModel={columnVisibilityConfig}
          setVisibilityModel={setColumnVisibilityConfig}
          setColumnConfigChanged={setColumnConfigChanged}
          mandatoryColumns={mandatoryColumns}
          setMandatoryColumns={setMandatoryColumns}
          colorColumns={colorColumns}
          setColorColumns={setColorColumns}
          projectId={projectId}
        />
      )}
      <TextBasedSpeedAuditShortcutModal
        isOpen={showShortcutModal}
        setIsOpen={setShowShortcutModal}
      />
      <TextBasedSpeedAuditFlagModal
        isOpen={showFlagDialog}
        fields={Object.keys(fields)}
        onRequestClose={() => {
          setShowFlagDialog(false);
          setMode(undefined);
        }}
        onFlag={(fields: string[], prompt?: string) => {
          setShowFlagDialog(false);
          flagTasks(fields, prompt);
        }}
      />
      <TextBasedSpeedAuditEditModal
        isOpen={showEditDialog}
        fields={Object.keys(fields)}
        onRequestClose={() => {
          setShowEditDialog(false);
          setMode(undefined);
        }}
        onEdit={(fields: string[], prompt?: string) => {
          setShowEditDialog(false);
          editField(fields, prompt);
        }}
      />
      {isTaskView && (
        <TaskView
          taskProps={selectedTaskProps}
          taskType={selectedTaskProps.type}
          nextButtonIsDisabled={selectedTaskIndex === attemptAuditsRef.current.length - 1}
          onClickNext={onViewNextTask}
          onClickPrevious={onViewPreviousTask}
          onClose={() => setIsTaskView(false)}
          previousButtonIsDisabled={selectedTaskIndex === 0}
          title={`Task ${selectedTaskIndex! + 1} of ${rows.length}`}
        />
      )}
      {!_.isEmpty(claimedTasksInfo) && (
        <AuditClaimedTasksDialog
          claimedTasksInfo={claimedTasksInfo}
          setClaimedTasksInfo={setClaimedTasksInfo}
          handleRefreshClaims={handleRefreshClaims}
        />
      )}
      <div className={styles.container}>
        <QAOperationContextProvider
          initialQAOperation={qaOperation}
          attemptResult={attemptAuditsRef.current.values()}
        >
          <TextBasedSpeedAuditHeader
            name={auditBatch.name}
            id={auditBatch._id}
            numObjects={objectIds.length}
            completed={completed}
            projectId={projectId}
            saveAudit={saveAudit}
            completeAudit={completeAudit}
            mode={mode}
            setMode={setMode}
            loading={loading}
            cellFontSize={cellFontSize}
            setCellFontSize={setCellFontSize}
            saveColumnConfig={async () =>
              await saveColumnConfig(
                projectId,
                columnConfig,
                columnVisibilityConfig,
                mandatoryColumns,
                colorColumns,
                true,
              )
            }
            collapseRows={collapseRows}
            setCollapseRows={setCollapseRows}
            isPodHealth={isPodHealth}
            setIsAuditing={setIsAuditing}
            canEditResponses={canEditResponses}
            setShowManageColumns={setShowManageColumns}
            exportDataAsCsv={exportDataAsCsv}
            setIsFindAndReplaceMode={setIsFindAndReplaceMode}
            updateCompletionCounts={updateCompletionCounts}
            mandatoryColumns={mandatoryColumns}
            apiRef={apiRef}
            columns={columns}
            usesPipelineV3={usesPipelineV3}
          />
        </QAOperationContextProvider>
        <div className={styles.datagrid}>
          <div className={styles.findAndReplaceDiv}>
            <FindAndReplace
              isOpen={isFindAndReplaceMode}
              onCancel={onCancelFindAndReplace}
              onClose={onCloseFindAndReplace}
              onFindAndReplace={onFindAndReplace}
              editableColumns={columns.filter(col => col.editable).map(col => col.field)}
              loading={loading}
            />
          </div>
          <DataGridPro
            className={styles.dataGridRoot}
            loading={loading}
            rows={rows}
            columns={columns}
            getRowId={row => row.task_links}
            getRowHeight={() => (collapseRows ? 160 : 'auto')}
            apiRef={apiRef}
            disableSelectionOnClick
            hideFooterRowCount
            onColumnWidthChange={handleColumnWidthChange}
            onColumnOrderChange={handleColumnOrderChange}
            columnVisibilityModel={columnVisibilityConfig}
            onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
            onCellClick={params =>
              params.field === 'rating'
                ? setRatingSelected(params.id as string)
                : setRatingSelected(undefined)
            }
            onCellEditStart={(params, event) => {
              if (
                event.type === 'keydown' &&
                (event as React.KeyboardEvent<HTMLElement>).key !== 'Enter'
              ) {
                event.defaultMuiPrevented = true;
              }
            }}
            onCellEditStop={(params, event) => {
              if (
                (event as React.KeyboardEvent<HTMLElement>).type === 'keydown' &&
                (event as React.KeyboardEvent<HTMLElement>).key === 'Backspace'
              ) {
                // params.value ca be a string or array
                const value: string = _.flatten([params.value])[0];
                const pastValue = { [params.field]: [value] };
                const curValue = { [params.field]: '' };
                const selection = window.getSelection();
                // If text is selected in cell then only delete the text
                if (selection && value === selection.anchorNode?.nodeValue) {
                  const { anchorOffset, focusOffset } = selection;
                  curValue[params.field] =
                    value.substring(0, anchorOffset) + value.substring(focusOffset);
                  apiRef.current.updateRows([{ task_links: params.id, ...curValue }]);
                }
                updatePastActions(
                  [{ task_links: params.id, ...curValue }],
                  [{ task_links: params.id, ...pastValue }],
                );
              }
            }}
            components={{ Footer: customFooterComponent }}
            componentsProps={{
              toolbar: {
                showQuickFilter: true,
              },
            }}
            getCellClassName={(params: GridCellParams<any, any, number>) => {
              const colorColumn = _.get(colorColumns, params.field);
              if (colorColumn && colorColumn.isColored) {
                if (colorColumn.isConditional) {
                  const conditionalSuccess = _.get(
                    ConditionOperations,
                    colorColumn.condition || '',
                    (conditionValue: $FIXME) =>
                      _.isArray(conditionValue) ? conditionValue.length : !!conditionValue,
                  )(params.value, colorColumn.conditionValue);
                  return (
                    (conditionalSuccess ? colorColumn.primaryColor : colorColumn.secondaryColor) ||
                    ''
                  );
                }
                return colorColumn.primaryColor || '';
              }
              return '';
            }}
          />
        </div>
      </div>
    </div>
  );
}

const useTextBasedSpeedAuditDialogStyles = makeStyles<{ cellFontSize: number }>(theme => ({
  fullPageModal: {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100vw',
    height: '100vh',
    background: 'white',
    zIndex: fullPageModalLayer,
    overflowY: 'auto',
  },
  container: {
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  datagrid: {
    marginTop: '20px',
    flexGrow: 1,
    '& .highlightedCell': {
      padding: '4px',
      backgroundColor: 'rgba(0, 255, 0, 0.5)',
    },
    '& .blue': {
      backgroundColor: '#b9d5ff91',
    },
    '& .orange': {
      backgroundColor: '#ff943975',
    },
    '& .violet': {
      backgroundColor: '#c671ff91',
    },
    '& .pink': {
      backgroundColor: '#f7cbf491',
    },
    '& .green': {
      backgroundColor: '#d6ffb991',
    },
    '& .light-blue': {
      backgroundColor: '#b9ffeb91',
    },
    '& .red': {
      backgroundColor: '#fe8e8e91',
    },
    '& .yellow': {
      backgroundColor: '#fef78e91',
    },
    '& .grey': {
      backgroundColor: '#9a9a9a91',
    },
    '& .lime': {
      backgroundColor: '#b4ff4691',
    },
    '& .standardCell': {},
  },
  progressBar: {
    height: '4px',
    background: theme.colors.blue,
    opacity: '0.5',
  },
  dataGridRoot: {
    '&.MuiDataGrid-root': {},
    '&.MuiDataGrid-root .MuiDataGrid-cell': {
      overflowX: 'hidden !important',
      overflowY: 'auto !important',
      alignItems: 'start !important',
      padding: theme.spacing(1),
      borderRight: '1px solid #f0f0f0',
    },
    '&.MuiDataGrid-root .MuiDataGrid-pinnedColumns': {
      backgroundColor: `#F8F8F8 !important`,
    },
    '&.MuiDataGrid-root .MuiDataGrid-pinnedColumnHeaders': {
      backgroundColor: `#F8F8F8 !important`,
    },
  },
  taskWrapComponent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  textWrap: {
    textOverflow: 'ellipsis',
    whiteSpace: 'break-spaces',
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    wordBreak: 'break-word',
  },
  diffLineHeight: {
    lineHeight: 1.9,
  },
  textWrapItem: {
    margin: 'auto',
    fontSize: props => props.cellFontSize,
  },
  resetWhiteSpace: {
    whiteSpace: 'normal',
  },
  markdownWrapItem: {
    margin: 'auto',
    fontSize: props => props.cellFontSize,
  },
  chipFontStyle: {
    fontSize: props => props.cellFontSize,
  },
  editedChip: {},
  sliderComponent: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    gap: '12px',
    padding: '8px',
  },
  sliderNumber: {
    maxWidth: '75px',
  },
  textFieldHelperText: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    taskWrapComponent: {
      display: 'flex',
      flexDirection: 'column',
    },
    taskButton: {
      width: 'fit-content',
      alignSelf: 'center',
    },
  },
  button: {
    color: 'rgb(25, 118, 210, 1)',
    background: 'white',
    borderColor: 'rgb(25, 118, 210, 1)',
    padding: '24px 16px !important',
  },
  textButton: {
    color: 'rgb(25, 118, 210, 1)',
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  findAndReplaceDiv: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row-reverse',
  },
  actionButton: {
    color: 'rgb(25, 118, 210, 1)',
    background: 'white',
    borderColor: 'rgb(25, 118, 210, 1)',
    padding: '6px 16px !important',
    '&:hover': {
      background: 'rgb(25, 118, 210, 0.25)',
    },
  },
}));

export default React.memo(TextBasedSpeedAuditDialog);
